import { Radio } from "lucide-react";
import { dayjs } from "@/utils/date";

interface LastCommunicationCardProps {
  /** LIC device identifier shown as the card title. */
  deviceIdentifier: string | null;
  /** Latest packet receipt time (ISO), or null when the device never reported. */
  latestPacketDate: string | null;
}

/**
 * Age thresholds (minutes) that tone the "Última comunicação" card.
 */
const RECENT_THRESHOLD_MINUTES = 15;
const STALE_THRESHOLD_MINUTES = 24 * 60;

/**
 * Renders the "Última comunicação" card: the age of the device's most recent
 * packet plus its absolute time, toned by how fresh the packet is.
 *
 * @param props - See {@link LastCommunicationCardProps}.
 */
export function LastCommunicationCard({ deviceIdentifier, latestPacketDate }: LastCommunicationCardProps) {
  const title = deviceIdentifier ? `Última comunicação · ${deviceIdentifier}` : "Última comunicação";

  if (!latestPacketDate) {
    return (
      <section className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-500">
            <Radio className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">Nenhuma comunicação recebida deste dispositivo.</p>
          </div>
        </div>
      </section>
    );
  }

  const packetDate = dayjs(latestPacketDate);
  const ageMinutes = Math.max(0, dayjs().diff(packetDate, "minute"));
  const tone =
    ageMinutes < RECENT_THRESHOLD_MINUTES
      ? { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Comunicação recente" }
      : ageMinutes < STALE_THRESHOLD_MINUTES
        ? { chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Sem comunicação recente" }
        : { chip: "bg-red-50 text-red-700 border-red-200", label: "Sem comunicação recente" };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-full ${
              ageMinutes < RECENT_THRESHOLD_MINUTES
                ? "bg-emerald-100 text-emerald-600"
                : "bg-neutral-100 text-neutral-500"
            }`}
          >
            <Radio className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600">
              {packetDate.calendar()} · há {packetDate.fromNow(true)}
            </p>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${tone.chip}`}
        >
          {tone.label}
        </span>
      </div>
    </section>
  );
}
